import Loader from '@/components/ui/loader';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { useMutateActivateClinicianTrial } from '@/hooks/mutation/useMutateActivateClinicianTrial';
import { getDictationModalQueuePorts } from '@heidi/dictate';
import { isDictationPremiumTrialEligibleAtom } from './atoms';

import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { Button } from '@heidi/design-system/button';
import { DialogFooter, DialogHeader } from '@heidi/design-system/dialog';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierProFilled } from '@heidi/design-system/icons';
import {
  TypographyFootNote,
  TypographyH2,
  TypographyP,
} from '@heidi/design-system/typography';
import { ModalId } from '@heidi/schema/ml-scribe';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useAtomValue } from 'jotai';
import { Blocks, Search, Sparkles } from 'lucide-react';
import { type ReactNode, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

const FeatureRow = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <div className="flex items-center gap-3">
    <div className="shrink-0 text-text-primary">{icon}</div>
    <TypographyP className="text-text-primary">{children}</TypographyP>
  </div>
);

export function DictationPremiumTrialModal() {
  const isEligible = useAtomValue(isDictationPremiumTrialEligibleAtom);
  const { isOpen, dismissible, handleClose } =
    getDictationModalQueuePorts().useQueuedModal(ModalId.CLINICIAN_TRIAL);
  const { mutate: activateClinicianTrial, isPending } = useMutateActivateClinicianTrial();

  const handlePrimaryClick = useCallback(() => {
    activateClinicianTrial(undefined, { onSettled: handleClose });
  }, [activateClinicianTrial, handleClose]);

  if (isAppleCompliantBuild() || !isEligible) {
    return null;
  }

  return (
    <DialogDrawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      dismissible={dismissible}
    >
      <DialogDrawerContent
        aria-describedby={undefined}
        className="mx-auto mb-auto flex w-full max-w-[480px] flex-col items-center justify-center gap-6 rounded-2xl border border-border bg-surface p-6 shadow-lg sm:min-w-[418px] sm:p-8"
        overlayClassName="backdrop-blur-sm"
        dismissible={dismissible}
      >
        <DialogHeader className="flex flex-col items-center gap-6">
          <FramedIcon state="pro" />
          <div className="flex flex-col items-center gap-2 text-center">
            <DialogTitle asChild>
              <TypographyH2 className="text-center text-text-primary">
                <FormattedMessage
                  defaultMessage="You've got 14 days to try everything"
                  id="dheRN6BXcu"
                />
              </TypographyH2>
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex w-full flex-col gap-4">
          <FeatureRow icon={<Blocks size={20} aria-hidden />}>
            <FormattedMessage
              defaultMessage="Custom templates built around your specialty and style"
              id="frbx3HLk8o"
            />
          </FeatureRow>
          <FeatureRow icon={<Sparkles size={20} aria-hidden />}>
            <FormattedMessage
              defaultMessage="Ask Heidi to edit notes, generate documents and answer clinical questions"
              id="uAw6omdEQa"
            />
          </FeatureRow>
          <FeatureRow icon={<Search size={20} aria-hidden />}>
            <FormattedMessage
              defaultMessage="In-session Evidence from trusted clinical sources"
              id="E6mZmR+w77"
            />
          </FeatureRow>
        </div>

        <DialogFooter className="flex w-full flex-col items-stretch pt-6 sm:flex-col">
          <div className="flex w-full flex-col gap-3">
            <Button
              className="w-full gap-2"
              variant="pro"
              size="lg"
              onClick={handlePrimaryClick}
              disabled={isPending}
            >
              {isPending ? (
                <Loader size={16} />
              ) : (
                <>
                  <TierProFilled aria-hidden />
                  <FormattedMessage defaultMessage="Start using Heidi" id="itAprWLCx2" />
                </>
              )}
            </Button>
            <TypographyFootNote className="text-center text-text-secondary">
              <FormattedMessage
                defaultMessage="Don't worry if the Clinician plan isn't for you. After your trial ends you can still use the Heidi Free plan."
                id="4IASLw6tvP"
              />
            </TypographyFootNote>
          </div>
        </DialogFooter>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
